import { useState, useCallback, useEffect } from 'react';
import type { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Stack,
  TextField,
  Alert,
  Typography,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { AccessTime as AccessTimeIcon, PlayCircleOutline as PlayCircleOutlineIcon } from '@mui/icons-material';

interface SegmentTimingFormProps {
  /** Current playback position in seconds (from player state). */
  currentTime: number;
  /** Called when the user saves a valid segment. */
  onSave: (startTime: number, endTime: number) => void;
  /** Called when the user cancels. */
  onCancel: () => void;
  /** True while the save/update mutation is in flight. */
  isSaving?: boolean;
  /** When provided, switches to edit mode: pre-populates fields, changes title/button copy. */
  initialStartTime?: number;
  /** When provided alongside initialStartTime, pre-populates end time field. */
  initialEndTime?: number;
  /** When provided, renders a "Preview Segment" button that calls this with valid times. */
  onPreview?: (startTime: number, endTime: number) => void;
}

interface FormState {
  startTime: string;
  endTime: string;
  startError: string;
  endError: string;
}

function parseSeconds(raw: string): number | null {
  const n = parseFloat(raw);
  return isNaN(n) || n < 0 ? null : n;
}

/**
 * SegmentTimingForm — inline form for creating or editing a segment's time range.
 *
 * Add mode (no initialStartTime): "Add Segment" title, "Save Segment" button.
 * Edit mode (initialStartTime provided): "Edit Segment" title, "Update Segment" button,
 *   fields pre-populated with current segment times.
 *
 * Both modes support a "Preview Segment" button (when onPreview is provided) that
 * seeks the player to startTime and plays to endTime then stops.
 *
 * Desktop (≥ md): each field row is single-line (input at 25% width + button).
 * Mobile: stacked layout (input above button).
 */
export const SegmentTimingForm = ({
  currentTime,
  onSave,
  onCancel,
  isSaving = false,
  initialStartTime,
  initialEndTime,
  onPreview,
}: SegmentTimingFormProps) => {
  const { t } = useTranslation('player');
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isEditMode = initialStartTime !== undefined;

  const [formState, setFormState] = useState<FormState>({
    startTime: initialStartTime !== undefined ? String(initialStartTime) : '',
    endTime: initialEndTime !== undefined ? String(initialEndTime) : '',
    startError: '',
    endError: '',
  });

  // Re-populate when the segment being edited changes (user picks a different chip).
  useEffect(() => {
    setFormState({
      startTime: initialStartTime !== undefined ? String(initialStartTime) : '',
      endTime: initialEndTime !== undefined ? String(initialEndTime) : '',
      startError: '',
      endError: '',
    });
  }, [initialStartTime, initialEndTime]);

  const handleStartChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setFormState((prev) => ({ ...prev, startTime: e.target.value, startError: '', endError: '' }));
  }, []);

  const handleEndChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setFormState((prev) => ({ ...prev, endTime: e.target.value, endError: '', startError: '' }));
  }, []);

  const handleUseCurrentTimeForStart = useCallback(() => {
    setFormState((prev) => ({
      ...prev,
      startTime: String(Math.floor(currentTime * 10) / 10),
      startError: '',
      endError: '',
    }));
  }, [currentTime]);

  const handleUseCurrentTimeForEnd = useCallback(() => {
    setFormState((prev) => ({
      ...prev,
      endTime: String(Math.floor(currentTime * 10) / 10),
      startError: '',
      endError: '',
    }));
  }, [currentTime]);

  const handleSave = useCallback(() => {
    const start = parseSeconds(formState.startTime);
    const end = parseSeconds(formState.endTime);
    if (start === null) {
      setFormState((prev) => ({ ...prev, startError: t('segmentInvalidStartTime') }));
      return;
    }
    if (end === null || end <= start) {
      setFormState((prev) => ({ ...prev, endError: t('segmentEndAfterStart') }));
      return;
    }
    onSave(start, end);
  }, [formState.startTime, formState.endTime, onSave, t]);

  const handlePreview = useCallback(() => {
    const start = parseSeconds(formState.startTime);
    const end = parseSeconds(formState.endTime);
    if (start !== null && end !== null && end > start && onPreview) {
      onPreview(start, end);
    }
  }, [formState.startTime, formState.endTime, onPreview]);

  const startVal = parseSeconds(formState.startTime);
  const endVal = parseSeconds(formState.endTime);
  const isValid = startVal !== null && endVal !== null && endVal > startVal;

  const renderFieldRow = (
    _field: 'start' | 'end',
    label: string,
    value: string,
    error: string,
    onChange: (e: ChangeEvent<HTMLInputElement>) => void,
    onUseCurrentTime: () => void,
  ) => {
    return (
      <Stack
        direction={isDesktop ? 'row' : 'column'}
        spacing={1}
        alignItems={isDesktop ? 'flex-start' : 'stretch'}
        width="100%"
      >
        <TextField
          label={label}
          value={value}
          onChange={onChange}
          type="number"
          size="small"
          inputProps={{ min: 0, step: 0.1 }}
          error={!!error}
          helperText={error || ' '}
          sx={{
            width: isDesktop ? '25%' : '100%',
            flexShrink: 0,
            '& .MuiInputBase-root': { minHeight: 44 },
          }}
        />
        {/* No aria-label on the button — accessible name comes from a visually
            hidden <span> inside the button containing the full label text. This
            prevents getByLabelText(/<fieldKey>/i) from matching the button while
            getByRole('button', { name: 'useCurrentTime — <key>' }) still works. */}
        <Button
          variant="outlined"
          size="small"
          onClick={onUseCurrentTime}
          startIcon={<AccessTimeIcon />}
          sx={{ minWidth: 44, minHeight: 44, mt: isDesktop ? 0.5 : 0, whiteSpace: 'nowrap', ...(isDesktop && { ml: 'auto' }) }}
        >
          <Box
            component="span"
            aria-hidden
            sx={{ display: 'inline' }}
          >
            {t('useCurrentTime')}
          </Box>
          <Box
            component="span"
            sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }}
          >
            {`${t('useCurrentTime')} — ${label}`}
          </Box>
        </Button>
      </Stack>
    );
  };

  return (
    <Box
      component="section"
      aria-label={isEditMode ? t('editSegment') : t('addSegment')}
      sx={{ px: 2, pt: 2, pb: 1.5 }}
    >
      <Typography variant="subtitle2" gutterBottom>
        {isEditMode ? t('editSegment') : t('addSegment')}
      </Typography>

      <Stack spacing={2}>
        {renderFieldRow(
          'start',
          t('segmentStartTime'),
          formState.startTime,
          formState.startError,
          handleStartChange,
          handleUseCurrentTimeForStart,
        )}

        {renderFieldRow(
          'end',
          t('segmentEndTime'),
          formState.endTime,
          formState.endError,
          handleEndChange,
          handleUseCurrentTimeForEnd,
        )}

        {formState.startTime !== '' && formState.endTime !== '' && !isValid && (
          <Alert severity="warning" sx={{ py: 0.5 }}>
            {t('segmentEndAfterStart')}
          </Alert>
        )}

        <Stack direction="row" spacing={1} alignItems="center" width="100%">
          {onPreview && (
            <Button
              variant="outlined"
              size="small"
              onClick={handlePreview}
              disabled={!isValid}
              startIcon={<PlayCircleOutlineIcon />}
              sx={{ minHeight: 44 }}
              aria-label={t('previewSegment', 'Preview Segment')}
            >
              {t('previewSegment', 'Preview Segment')}
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            variant="text"
            onClick={onCancel}
            disabled={isSaving}
            sx={{ minHeight: 44 }}
          >
            {t('cancelSegment')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!isValid || isSaving}
            sx={{ minHeight: 44 }}
          >
            {isSaving
              ? (isEditMode ? t('updateSegment') : t('saveSegment')) + '…'
              : isEditMode ? t('updateSegment') : t('saveSegment')}
          </Button>
        </Stack>
      </Stack>

      <Divider sx={{ mt: 2 }} />
    </Box>
  );
};
